import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useInvoiceDetail } from '../api/useInvoiceDetail';
import { useInvoices } from '../api/useInvoices';
import { useEditInvoice } from '../api/useEditInvoice';
import { extractApiErrorMessage } from '../api/client';
import { extractString } from '../utils/sanitizeData';
import {
  ALLOW_LIST_FIELDS,
  getFieldType,
  buildPatchPayload,
  HeaderFieldForm,
  LineItemForm,
} from '../utils/invoiceEditPayload';

export default function InvoiceEditPage() {
  const { id: routeId } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Determine target invoice public token
  const queryId = searchParams.get('id');
  const targetToken = routeId && routeId !== 'edit' ? routeId : queryId || '';

  // Invoices list for fallback selector when visiting /invoices/edit standalone
  const { data: invoicesList } = useInvoices();
  const availableInvoices = invoicesList?.results || [];

  const effectiveToken = targetToken || (availableInvoices.length > 0 ? availableInvoices[0].id : '');

  // Fetch live invoice details from API v2
  const { data: invoice, isLoading: isInvoiceLoading } = useInvoiceDetail(effectiveToken);
  const editInvoice = useEditInvoice();

  // State management
  const [initialHeaderFields, setInitialHeaderFields] = useState<Record<string, string>>({});
  const [initialLineItems, setInitialLineItems] = useState<LineItemForm[]>([]);
  const [headerFields, setHeaderFields] = useState<HeaderFieldForm[]>([]);
  const [lineItems, setLineItems] = useState<LineItemForm[]>([]);
  const [currency, setCurrency] = useState('EUR');
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const backUrl = effectiveToken ? `/invoices/${effectiveToken}` : '/invoices';

  // Synchronize live API data into editable form states
  useEffect(() => {
    if (!invoice) return;

    const initialMap: Record<string, string> = {};
    const fieldsList: HeaderFieldForm[] = [];

    const addExistingField = (key: string, rawVal: unknown) => {
      const val = extractString(rawVal);
      if (val && !initialMap[key]) {
        initialMap[key] = val;
        fieldsList.push({
          id: `field-${key}`,
          key,
          value: val,
          type: getFieldType(key),
          isNew: false,
        });
      }
    };

    // 1. Identifiers & Vendor
    addExistingField(
      'InvoiceId',
      invoice.fields?.invoice_meta?.invoice_id ||
        invoice.fields?.invoice_id ||
        (invoice.fields?.additional_fields as Record<string, unknown> | undefined)?.['InvoiceId']
    );
    addExistingField('VendorName', invoice.vendor_name || invoice.fields?.vendor?.name);
    addExistingField('PurchaseOrder', invoice.fields?.invoice_meta?.purchase_order || invoice.fields?.po_number);

    // 2. Dates
    addExistingField('InvoiceDate', invoice.invoice_date || invoice.fields?.invoice_meta?.invoice_date);
    addExistingField('DueDate', invoice.due_date || invoice.fields?.invoice_meta?.due_date);

    // 3. Customer
    addExistingField('CustomerName', invoice.fields?.customer?.name || invoice.fields?.customer_name);
    addExistingField('CustomerTaxId', invoice.fields?.customer?.tax_id || invoice.fields?.customer_vat);
    addExistingField('CustomerAddress', invoice.fields?.customer?.address || invoice.fields?.customer_address);

    // 4. Vendor Details
    addExistingField('VendorAddress', invoice.fields?.vendor?.address || invoice.fields?.vendor_address);
    addExistingField('VendorTaxId', invoice.fields?.vendor?.tax_id || invoice.fields?.vendor_vat);
    addExistingField('BillingEmail', invoice.fields?.vendor?.email || invoice.fields?.customer?.email || invoice.fields?.billing_email);

    // 5. Totals
    const cur = extractString(invoice.invoice_total?.currency || invoice.fields?.totals?.currency, 'EUR');
    setCurrency(cur);

    addExistingField('SubTotal', invoice.fields?.totals?.subtotal || invoice.fields?.subtotal);
    addExistingField('TotalTax', invoice.fields?.totals?.total_tax || invoice.fields?.tax_amount);
    addExistingField('InvoiceTotal', invoice.invoice_total?.amount || invoice.fields?.totals?.invoice_total);
    addExistingField('AmountDue', invoice.fields?.totals?.amount_due || invoice.fields?.amount_due);

    // 6. Dynamic Additional Fields (from fields.additional_fields)
    if (invoice.fields?.additional_fields && typeof invoice.fields.additional_fields === 'object') {
      Object.entries(invoice.fields.additional_fields).forEach(([k, v]) => {
        if (v !== null && v !== undefined && !initialMap[k]) {
          addExistingField(k, v);
        }
      });
    }

    setInitialHeaderFields(initialMap);
    if (fieldsList.length > 0) {
      setHeaderFields(fieldsList);
    }

    // Populate Line Items from API (with 0-based originalIndex)
    if (invoice.items && Array.isArray(invoice.items) && invoice.items.length > 0) {
      const mappedItems: LineItemForm[] = invoice.items.map((item, idx) => ({
        id: extractString(item.id, `item-${idx}`),
        originalIndex: idx,
        description: extractString(item.description, 'Line Item'),
        quantity: extractString(item.quantity, '1'),
        unitPrice: extractString(item.unit_price ?? item.amount, '0.00'),
        amount: extractString(item.amount, '0.00'),
        isDeleted: false,
      }));
      setLineItems(mappedItems);
      setInitialLineItems(mappedItems);
    } else {
      setLineItems([]);
      setInitialLineItems([]);
    }
  }, [invoice]);

  // Handle header field updates
  const handleFieldChange = (fieldId: string, newValue: string) => {
    setHeaderFields((prev) =>
      prev.map((f) => (f.id === fieldId ? { ...f, value: newValue } : f))
    );
  };

  const handleSelectNewFieldKey = (fieldId: string, newKey: string) => {
    setHeaderFields((prev) =>
      prev.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              key: newKey,
              type: getFieldType(newKey),
            }
          : f
      )
    );
  };

  // Add new row for allow-listed header field
  const handleAddNewHeaderField = () => {
    // Find first available unmapped key
    const usedKeys = headerFields.map((f) => f.key.toLowerCase());
    const firstAvailable = ALLOW_LIST_FIELDS.find((f) => !usedKeys.includes(f.key.toLowerCase()));
    const key = firstAvailable ? firstAvailable.key : '';

    const newField: HeaderFieldForm = {
      id: `new-field-${Date.now()}`,
      key,
      value: '',
      type: key ? getFieldType(key) : 'text',
      isNew: true,
    };
    setHeaderFields((prev) => [...prev, newField]);
  };

  const handleRemoveNewHeaderField = (fieldId: string) => {
    setHeaderFields((prev) => prev.filter((f) => f.id !== fieldId));
  };

  // Handle line item updates with auto-calculation
  const handleLineItemChange = (
    itemId: string,
    field: keyof LineItemForm,
    newValue: string
  ) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const updated = { ...item, [field]: newValue };

        // Auto-calculate amount if quantity or unitPrice is modified
        if (field === 'quantity' || field === 'unitPrice') {
          const q = parseFloat(updated.quantity.replace(/[^0-9.-]/g, ''));
          const u = parseFloat(updated.unitPrice.replace(/[^0-9.-]/g, ''));
          if (!isNaN(q) && !isNaN(u) && q > 0 && u >= 0) {
            updated.amount = (q * u).toFixed(2);
          }
        }
        return updated;
      })
    );
  };

  const handleAddNewLineItem = () => {
    const newItem: LineItemForm = {
      id: `new-item-${Date.now()}`,
      originalIndex: undefined, // brand new item
      description: '',
      quantity: '1',
      unitPrice: '0.00',
      amount: '0.00',
      isDeleted: false,
    };
    setLineItems((prev) => [...prev, newItem]);
  };

  const handleDeleteLineItem = (itemId: string) => {
    setLineItems((prev) =>
      prev
        .map((item) => {
          if (item.id !== itemId) return item;
          // If existing server item, mark for deletion to send original index in remove_items
          if (item.originalIndex !== undefined) {
            return { ...item, isDeleted: true };
          }
          // If brand new item, mark as deleted so filter drops it
          return { ...item, isDeleted: true };
        })
        .filter((item) => item.originalIndex !== undefined || !item.isDeleted)
    );
  };

  const handleRestoreLineItem = (itemId: string) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, isDeleted: false } : item))
    );
  };

  // Live calculations
  const activeLineItems = useMemo(
    () => lineItems.filter((item) => !item.isDeleted),
    [lineItems]
  );

  const lineItemsTotal = useMemo(() => {
    return activeLineItems.reduce((acc, curr) => {
      const rawNum = curr.amount.replace(/[^0-9.-]/g, '');
      const val = parseFloat(rawNum);
      return acc + (isNaN(val) ? 0 : val);
    }, 0);
  }, [activeLineItems]);

  // Check mismatch against InvoiceTotal header field
  const invoiceTotalField = headerFields.find(
    (f) => f.key.toLowerCase() === 'invoicetotal'
  );
  const headerInvoiceTotal = invoiceTotalField
    ? parseFloat(invoiceTotalField.value.replace(/[^0-9.-]/g, ''))
    : NaN;

  const hasMismatch =
    !isNaN(headerInvoiceTotal) &&
    activeLineItems.length > 0 &&
    Math.abs(lineItemsTotal - headerInvoiceTotal) > 0.01;

  // Compute unmapped allow-list options for the add-field selector
  const usedKeys = useMemo(
    () => headerFields.map((f) => f.key.toLowerCase()),
    [headerFields]
  );

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setErrorMessage(null);

    // Build strictly typed PATCH delta payload per specification
    const payload = buildPatchPayload(initialHeaderFields, headerFields, lineItems, initialLineItems);

    try {
      await editInvoice.mutateAsync({
        publicToken: effectiveToken,
        payload,
      });

      setSavedSuccess(true);
      setTimeout(() => {
        navigate(backUrl);
      }, 1200);
    } catch (err: unknown) {
      if (effectiveToken.startsWith('github') || effectiveToken.startsWith('msft')) {
        // Demo mode fallback
        setSavedSuccess(true);
        setTimeout(() => {
          navigate(backUrl);
        }, 1200);
        return;
      }

      const errorMsg = extractApiErrorMessage(err);
      setErrorMessage(errorMsg);
    }
  };

  const renderTypeBadge = (type: HeaderFieldForm['type']) => {
    switch (type) {
      case 'ref':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
            ref
          </span>
        );
      case 'date':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
            date
          </span>
        );
      case 'amount':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
            amount
          </span>
        );
      case 'address':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800">
            address
          </span>
        );
      case 'text':
      default:
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-gray-100 text-gray-700 dark:bg-strokedark dark:text-bodydark border border-gray-200 dark:border-strokedark">
            text
          </span>
        );
    }
  };

  return (
    <div className="max-w-[1180px] mx-auto py-2 sm:py-6 px-2 sm:px-4">
      {/* Back Link & Standalone Selector */}
      <div className="mb-4 flex items-center justify-between">
        <Link
          to={backUrl}
          className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-brand-vib hover:underline"
        >
          <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Invoice
        </Link>

        {/* Invoice Selector (if visiting /invoices/edit without specific ID) */}
        {!routeId && availableInvoices.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-brand-soft">Invoice:</span>
            <select
              value={effectiveToken}
              onChange={(e) => setSearchParams({ id: e.target.value })}
              className="text-xs font-mono bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark rounded-lg px-2.5 py-1 text-brand-navy dark:text-white"
            >
              {availableInvoices.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  {inv.vendor_name || 'Vendor'} — {inv.invoice_date || 'Date'}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Top Header & Action Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-brand-line dark:border-strokedark">
        <div>
          <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
            / VALIDATE INVOICE
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight mt-0.5">
            Edit Data
          </h1>
          <p className="text-xs sm:text-sm text-brand-muted dark:text-bodydark mt-1">
            Editing {invoice?.vendor_name ? `"${invoice.vendor_name}"` : 'Invoice'} fields and line items. Changes save as a new revision.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to={backUrl}
            className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-brand-muted hover:text-brand-navy dark:text-bodydark dark:hover:text-white transition-colors"
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => handleSave()}
            disabled={editInvoice.isPending || isInvoiceLoading}
            className="px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414 1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {editInvoice.isPending ? 'Saving…' : 'Save Corrections'}
          </button>
        </div>
      </div>

      {/* Synchronizing Feedback */}
      {isInvoiceLoading && (
        <div className="mb-4 text-xs font-mono text-brand-soft flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-brand-vib animate-ping" />
          <span>Synchronizing live invoice details from API…</span>
        </div>
      )}

      {/* Success Notification */}
      {savedSuccess && (
        <div className="mb-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs font-mono text-emerald-800 dark:text-emerald-300 shadow-sm animate-fadeIn">
          ✅ Corrections successfully saved! Redirecting to invoice details…
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-6 p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs font-mono text-rose-700 dark:text-rose-300 shadow-sm">
          ❌ {errorMessage}
        </div>
      )}

      {/* Advisory Total Mismatch Warning */}
      {hasMismatch && (
        <div className="mb-6 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs font-mono text-amber-800 dark:text-amber-300 shadow-sm flex items-start gap-2.5">
          <span className="text-base">⚠️</span>
          <div>
            <span className="font-bold">Total Mismatch Advisory: </span>
            Line items total ({lineItemsTotal.toFixed(2)} {currency}) doesn&rsquo;t match the InvoiceTotal header field ({headerInvoiceTotal.toFixed(2)} {currency}). Double-check your numbers before saving (advisory, will not block save).
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-8">
        {/* SECTION 1: HEADER FIELDS */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-brand-line dark:border-strokedark flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-brand-vib" />
              <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                Header Fields
              </h2>
              <span className="text-xs font-mono text-brand-soft">
                ({headerFields.length})
              </span>
            </div>
            <button
              type="button"
              onClick={handleAddNewHeaderField}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-semibold bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white transition-colors border border-brand-vib/20 cursor-pointer"
            >
              + Add Field
            </button>
          </div>

          {/* MOBILE VIEW: Stacked Cards for spacious full-width inputs */}
          <div className="sm:hidden p-3 space-y-3">
            {headerFields.map((f) => (
              <div
                key={f.id}
                className="p-3.5 rounded-2xl bg-brand-bg/40 dark:bg-strokedark/30 border border-brand-line/70 dark:border-strokedark space-y-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {renderTypeBadge(f.type)}
                    {f.isNew ? (
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <select
                          value={f.key}
                          onChange={(e) => handleSelectNewFieldKey(f.id, e.target.value)}
                          className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white dark:bg-form-input border border-brand-vib/40 text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib font-semibold truncate"
                        >
                          <option value="" disabled>
                            Select field to add…
                          </option>
                          {ALLOW_LIST_FIELDS.filter(
                            (opt) => opt.key === f.key || !usedKeys.includes(opt.key.toLowerCase())
                          ).map((opt) => (
                            <option key={opt.key} value={opt.key}>
                              {opt.key} ({opt.label})
                            </option>
                          ))}
                        </select>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 uppercase tracking-wider shrink-0">
                          NEW
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs font-mono font-bold text-brand-navy dark:text-white truncate">
                        {f.key}
                      </span>
                    )}
                  </div>
                  {f.isNew && (
                    <button
                      type="button"
                      onClick={() => handleRemoveNewHeaderField(f.id)}
                      className="text-brand-soft hover:text-rose-600 transition-colors p-1"
                      title="Remove field"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Full-width spacious input for mobile */}
                <div>
                  <input
                    type="text"
                    value={f.value}
                    onChange={(e) => handleFieldChange(f.id, e.target.value)}
                    placeholder={`Enter ${f.key || 'value'}…`}
                    className="w-full px-3.5 py-2.5 rounded-xl text-sm font-mono bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* DESKTOP VIEW: Clean multi-column table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-brand-blue-tint/60 dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark">
                  <th className="py-3 px-5 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-24">
                    Type
                  </th>
                  <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-60">
                    Field
                  </th>
                  <th className="py-3 px-5 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                    Value
                  </th>
                  <th className="py-3 px-3 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-12 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
                {headerFields.map((f) => (
                  <tr
                    key={f.id}
                    className="hover:bg-brand-bg/60 dark:hover:bg-strokedark/30 transition-colors"
                  >
                    <td className="py-3 px-5">{renderTypeBadge(f.type)}</td>
                    <td className="py-3 px-4">
                      {f.isNew ? (
                        <div className="flex items-center gap-1.5">
                          <select
                            value={f.key}
                            onChange={(e) => handleSelectNewFieldKey(f.id, e.target.value)}
                            className="w-full px-2.5 py-1.5 rounded-lg text-xs bg-white dark:bg-form-input border border-brand-vib/40 text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib font-semibold"
                          >
                            <option value="" disabled>
                              Select field to add…
                            </option>
                            {ALLOW_LIST_FIELDS.filter(
                              (opt) => opt.key === f.key || !usedKeys.includes(opt.key.toLowerCase())
                            ).map((opt) => (
                              <option key={opt.key} value={opt.key}>
                                {opt.key} ({opt.label})
                              </option>
                            ))}
                          </select>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 uppercase tracking-wider">
                            NEW
                          </span>
                        </div>
                      ) : (
                        <span className="text-brand-navy dark:text-white font-semibold">
                          {f.key}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-5">
                      <input
                        type="text"
                        value={f.value}
                        onChange={(e) => handleFieldChange(f.id, e.target.value)}
                        placeholder={`Enter ${f.key || 'value'}…`}
                        className="w-full px-3 py-1.5 rounded-lg text-xs bg-brand-bg dark:bg-strokedark/50 border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                      />
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      {f.isNew ? (
                        <button
                          type="button"
                          onClick={() => handleRemoveNewHeaderField(f.id)}
                          className="text-brand-soft hover:text-rose-600 transition-colors cursor-pointer p-1"
                          title="Remove new field"
                        >
                          ✕
                        </button>
                      ) : (
                        <span
                          className="text-[10px] text-brand-soft/50 dark:text-bodydark2/50 select-none cursor-default"
                          title="Existing schema fields cannot be deleted"
                        >
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* SECTION 2: LINE ITEMS GRID */}
        <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-card overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-brand-line dark:border-strokedark flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-brand-vib" />
              <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                Extracted Line Items ({activeLineItems.length})
              </h2>
              <span className="text-xs font-mono font-bold text-brand-navy dark:text-white bg-brand-blue-tint/50 dark:bg-brand-900/20 px-2 py-0.5 rounded-md border border-brand-vib/20">
                Line Items Total: {lineItemsTotal.toFixed(2)} {currency}
              </span>
            </div>
            <button
              type="button"
              onClick={handleAddNewLineItem}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-semibold bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white transition-colors border border-brand-vib/20 cursor-pointer"
            >
              + Add Line Item
            </button>
          </div>

          {/* MOBILE VIEW: Stacked Line Item Cards */}
          <div className="sm:hidden p-3 space-y-3">
            {lineItems.length === 0 ? (
              <div className="p-6 text-center text-xs font-mono text-brand-soft italic">
                No line items parsed for this invoice. Click &ldquo;+ Add Line Item&rdquo; above to append rows.
              </div>
            ) : (
              lineItems.map((item, idx) => {
                const isNew = item.originalIndex === undefined;
                const isDeleted = item.isDeleted === true;

                return (
                  <div
                    key={item.id}
                    className={`p-3.5 rounded-2xl border transition-colors space-y-3 ${
                      isDeleted
                        ? 'bg-rose-50/40 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800 opacity-60'
                        : isNew
                        ? 'bg-emerald-50/20 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-800'
                        : 'bg-brand-bg/40 dark:bg-strokedark/30 border-brand-line/70 dark:border-strokedark'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-brand-blue-tint text-brand-vib text-xs font-mono font-bold">
                          #{idx + 1}
                        </span>
                        {isNew && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 uppercase tracking-wider">
                            NEW
                          </span>
                        )}
                        {isDeleted && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 uppercase tracking-wider">
                            DELETED
                          </span>
                        )}
                      </div>
                      <div>
                        {isDeleted ? (
                          <button
                            type="button"
                            onClick={() => handleRestoreLineItem(item.id)}
                            className="text-xs font-semibold text-brand-vib hover:underline cursor-pointer"
                          >
                            Undo
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleDeleteLineItem(item.id)}
                            className="text-brand-soft hover:text-rose-600 transition-colors p-1"
                            title="Delete line item"
                          >
                            <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Full-width Description */}
                    <div>
                      <label className="block text-[10px] uppercase font-mono text-brand-soft mb-1 font-semibold">
                        Description *
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        disabled={isDeleted}
                        placeholder="Line item description (required)…"
                        onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                        className={`w-full px-3.5 py-2.5 rounded-xl text-sm font-mono bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs ${
                          isDeleted ? 'line-through text-brand-soft' : ''
                        }`}
                      />
                    </div>

                    {/* 3 Columns for Qty, Unit Price, Amount */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-[10px] uppercase font-mono text-brand-soft mb-1 font-semibold">
                          Qty
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.quantity}
                          disabled={isDeleted}
                          onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                          className="w-full px-2.5 py-2 rounded-xl text-xs font-mono text-right bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-mono text-brand-soft mb-1 font-semibold">
                          Unit Price
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.unitPrice}
                          disabled={isDeleted}
                          onChange={(e) => handleLineItemChange(item.id, 'unitPrice', e.target.value)}
                          className="w-full px-2.5 py-2 rounded-xl text-xs font-mono text-right bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] uppercase font-mono text-brand-soft mb-1 font-semibold">
                          Amount
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.amount}
                          disabled={isDeleted}
                          onChange={(e) => handleLineItemChange(item.id, 'amount', e.target.value)}
                          className={`w-full px-2.5 py-2 rounded-xl text-xs font-mono font-bold text-right bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs ${
                            isDeleted ? 'line-through' : ''
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* DESKTOP VIEW: Multi-column table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-brand-blue-tint/60 dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark">
                  <th className="py-3 px-3 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-10 text-center">
                    #
                  </th>
                  <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                    Description *
                  </th>
                  <th className="py-3 px-3 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-24 text-right">
                    Quantity
                  </th>
                  <th className="py-3 px-3 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-28 text-right">
                    Unit Price
                  </th>
                  <th className="py-3 px-4 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-32 text-right">
                    Amount
                  </th>
                  <th className="py-3 px-3 text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300 w-16 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
                {lineItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-brand-soft italic">
                      No line items parsed for this invoice. Click &ldquo;+ Add Line Item&rdquo; above to append rows.
                    </td>
                  </tr>
                ) : (
                  lineItems.map((item, idx) => {
                    const isNew = item.originalIndex === undefined;
                    const isDeleted = item.isDeleted === true;

                    return (
                      <tr
                        key={item.id}
                        className={`transition-colors ${
                          isDeleted
                            ? 'bg-rose-50/40 dark:bg-rose-950/20 opacity-60'
                            : isNew
                            ? 'bg-emerald-50/20 dark:bg-emerald-950/10 hover:bg-emerald-50/40'
                            : 'hover:bg-brand-bg/60 dark:hover:bg-strokedark/30'
                        }`}
                      >
                        <td className="py-2.5 px-3 text-center text-brand-soft font-semibold">
                          {idx + 1}
                        </td>
                        <td className="py-2.5 px-4">
                          <div className="flex items-center gap-2">
                            {isNew && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 uppercase tracking-wider">
                                NEW
                              </span>
                            )}
                            <input
                              type="text"
                              value={item.description}
                              disabled={isDeleted}
                              placeholder="Line item description (required)…"
                              onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                              className={`w-full px-3 py-1.5 rounded-lg text-xs bg-brand-bg dark:bg-strokedark/50 border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib ${
                                isDeleted ? 'line-through text-brand-soft' : ''
                              }`}
                            />
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.quantity}
                            disabled={isDeleted}
                            onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                            className="w-full text-right px-2.5 py-1.5 rounded-lg text-xs bg-brand-bg dark:bg-strokedark/50 border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                          />
                        </td>
                        <td className="py-2.5 px-3">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.unitPrice}
                            disabled={isDeleted}
                            onChange={(e) => handleLineItemChange(item.id, 'unitPrice', e.target.value)}
                            className="w-full text-right px-2.5 py-1.5 rounded-lg text-xs bg-brand-bg dark:bg-strokedark/50 border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                          />
                        </td>
                        <td className="py-2.5 px-4">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.amount}
                            disabled={isDeleted}
                            onChange={(e) => handleLineItemChange(item.id, 'amount', e.target.value)}
                            className={`w-full text-right px-2.5 py-1.5 rounded-lg text-xs bg-brand-bg dark:bg-strokedark/50 border border-brand-line dark:border-strokedark text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib font-semibold ${
                              isDeleted ? 'line-through' : ''
                            }`}
                          />
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          {isDeleted ? (
                            <button
                              type="button"
                              onClick={() => handleRestoreLineItem(item.id)}
                              className="text-xs font-semibold text-brand-vib hover:underline cursor-pointer"
                              title="Restore deleted item"
                            >
                              Undo
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleDeleteLineItem(item.id)}
                              className="text-brand-soft hover:text-rose-600 transition-colors cursor-pointer p-1"
                              title="Delete line item"
                            >
                              <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sticky Bottom Save Controls */}
        <div className="sticky bottom-4 z-20 flex items-center justify-between p-4 rounded-2xl bg-white/95 dark:bg-boxdark/95 backdrop-blur-md border border-brand-line dark:border-strokedark shadow-xl">
          <div className="text-xs font-mono text-brand-soft hidden sm:block">
            All edits will be committed as a new timestamped version.
          </div>
          <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto gap-3">
            <Link
              to={backUrl}
              className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-brand-muted hover:text-brand-navy dark:text-bodydark dark:hover:text-white transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={editInvoice.isPending || isInvoiceLoading}
              className="px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-md inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414 1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {editInvoice.isPending ? 'Saving…' : 'Save Corrections'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
